import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { toast } from "sonner";
import {
  Upload, Download, CheckCircle2, XCircle, AlertCircle, Clock,
  FileSpreadsheet, ArrowRight, RotateCcw, Users, Contact2,
  Truck, TrendingUp, Package, DollarSign, ChevronDown, ChevronUp,
  Eye, Play, Loader2, X, RefreshCw, History,
} from "lucide-react";
import api from "@/services/api";

// ── API helpers ───────────────────────────────────────────────────────────────
const importApi = {
  listSessions:   (params) => api.get("/admin/imports/sessions", { params }),
  getSession:     (id)     => api.get(`/admin/imports/sessions/${id}`),
  getSchema:      (type)   => api.get(`/admin/imports/schema/${type}`),
  uploadSession:  (form)   => api.post("/admin/imports/sessions", form, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  validate:       (id)     => api.post(`/admin/imports/sessions/${id}/validate`),
  commit:         (id)     => api.post(`/admin/imports/sessions/${id}/commit`, {}),
  cancel:         (id)     => api.post(`/admin/imports/sessions/${id}/cancel`),
};

// ── Entity type config ────────────────────────────────────────────────────────
const ENTITIES = [
  {
    type: "customers",       label: "Customers",                 icon: Users,
    color: "#0176D3",        bgColor: "#EEF4FF",
    desc: "CRM accounts — individuals and organisations",
  },
  {
    type: "contacts",        label: "Contacts",                  icon: Contact2,
    color: "#2E844A",        bgColor: "#EEF6EC",
    desc: "People linked to CRM accounts",
  },
  {
    type: "suppliers",       label: "Suppliers & Vendors",       icon: Truck,
    color: "#FF8800",        bgColor: "#FFF4E0",
    desc: "Event service providers and vendors",
  },
  {
    type: "leads",           label: "Leads",                     icon: TrendingUp,
    color: "#9333EA",        bgColor: "#F5F0FF",
    desc: "Enquiries and prospective customers",
  },
  {
    type: "service_catalog", label: "Service Catalog",           icon: Package,
    color: "#0776C1",        bgColor: "#E6F4FF",
    desc: "Service definitions added to the Event Model",
  },
  {
    type: "opening_balances",label: "Opening Balances",          icon: DollarSign,
    color: "#C23934",        bgColor: "#FEF2F0",
    desc: "Financial opening balances — UNPOSTED until Finance authorises",
  },
];

const STATUS_CONFIG = {
  UPLOADED:       { color: "#0176D3", bg: "#EEF4FF", label: "Uploaded"        },
  VALIDATED:      { color: "#2E844A", bg: "#EEF6EC", label: "Dry Run Complete" },
  COMMITTED:      { color: "#2E844A", bg: "#EEF6EC", label: "Committed"        },
  PARTIAL_COMMIT: { color: "#FF8800", bg: "#FFF4E0", label: "Partial Commit"   },
  CANCELLED:      { color: "#706E6B", bg: "#F3F3F3", label: "Cancelled"        },
  FAILED:         { color: "#C23934", bg: "#FEF2F0", label: "Failed"           },
};

const ROW_STATUS_CONFIG = {
  OK:        { color: "#2E844A", label: "OK"        },
  DUPLICATE: { color: "#FF8800", label: "Duplicate" },
  ERROR:     { color: "#C23934", label: "Error"     },
  COMMITTED: { color: "#2E844A", label: "Committed" },
  SKIPPED:   { color: "#706E6B", label: "Skipped"   },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { color: "#706E6B", bg: "#F3F3F3", label: status };
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function RowStatusBadge({ status }) {
  const cfg = ROW_STATUS_CONFIG[status] || { color: "#706E6B", label: status };
  return (
    <span className="text-xs font-bold" style={{ color: cfg.color }}>{cfg.label}</span>
  );
}

// ── Step indicator ────────────────────────────────────────────────────────────
const STEPS = ["Select Type", "Upload File", "Preview & Validate", "Commit"];
function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
            i < current ? "bg-[#2E844A] text-white" :
            i === current ? "bg-[#0176D3] text-white" :
            "bg-[#E5E5E5] text-[#706E6B]"
          }`}>
            {i < current ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
          </div>
          <span className={`text-xs font-medium hidden sm:inline ${
            i === current ? "text-[#0176D3]" : i < current ? "text-[#2E844A]" : "text-[#706E6B]"
          }`}>{s}</span>
          {i < STEPS.length - 1 && <ArrowRight className="w-3 h-3 text-[#D0D0D0]" />}
        </div>
      ))}
    </div>
  );
}

// ── Row detail table ──────────────────────────────────────────────────────────
function RowsTable({ rows, schema }) {
  const [expanded, setExpanded] = useState(null);
  const cols = (schema?.columns || []).slice(0, 4); // show first 4 cols

  return (
    <div className="overflow-x-auto rounded-xl border border-[#E5E5E5]">
      <table className="w-full text-xs">
        <thead className="bg-[#F3F3F3] border-b border-[#E5E5E5]">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-[#706E6B]">#</th>
            <th className="px-3 py-2 text-left font-semibold text-[#706E6B]">Status</th>
            {cols.map(c => (
              <th key={c.col} className="px-3 py-2 text-left font-semibold text-[#706E6B]">{c.label}</th>
            ))}
            <th className="px-3 py-2 text-left font-semibold text-[#706E6B]">Issues</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <>
              <tr key={r.row_num}
                  className={`border-b border-[#E5E5E5] cursor-pointer hover:bg-[#F9F9F9] transition-colors ${
                    r.status === "ERROR" ? "bg-[#FEF9F9]" : r.status === "DUPLICATE" ? "bg-[#FFFBF0]" : ""
                  }`}
                  onClick={() => setExpanded(expanded === r.row_num ? null : r.row_num)}
                  data-testid={`import-row-${r.row_num}`}>
                <td className="px-3 py-2 text-[#706E6B]">{r.row_num}</td>
                <td className="px-3 py-2"><RowStatusBadge status={r.status} /></td>
                {cols.map(c => (
                  <td key={c.col} className="px-3 py-2 text-[#181818] max-w-[150px] truncate">
                    {r.data?.[c.col] || <span className="text-[#AAAAAA]">—</span>}
                  </td>
                ))}
                <td className="px-3 py-2">
                  {r.errors?.length > 0 && (
                    <span className="text-[#C23934] font-medium">{r.errors.length} error{r.errors.length > 1 ? "s" : ""}</span>
                  )}
                  {r.warnings?.filter(w => !w.includes("UNPOSTED")).length > 0 && r.errors?.length === 0 && (
                    <span className="text-[#FF8800] font-medium">{r.warnings.filter(w => !w.includes("UNPOSTED")).length} warning{r.warnings.filter(w => !w.includes("UNPOSTED")).length > 1 ? "s" : ""}</span>
                  )}
                  {(!r.errors?.length && !r.warnings?.filter(w => !w.includes("UNPOSTED")).length) && (
                    <span className="text-[#2E844A]">Clean</span>
                  )}
                  <span className="ml-1 text-[#D0D0D0]">{expanded === r.row_num ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />}</span>
                </td>
              </tr>
              {expanded === r.row_num && (r.errors?.length > 0 || r.warnings?.length > 0) && (
                <tr key={`${r.row_num}-detail`} className="bg-[#F9F9F9]">
                  <td colSpan={6 + cols.length} className="px-6 py-3">
                    {r.errors?.map((e, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-[#C23934] mb-1">
                        <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>{e}</span>
                      </div>
                    ))}
                    {r.warnings?.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-[#FF8800] mb-1">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── History panel ─────────────────────────────────────────────────────────────
function HistoryPanel({ sessions, onSelect, loading }) {
  return (
    <div className="space-y-2" data-testid="import-history-panel">
      {loading && <div className="text-sm text-[#706E6B] animate-pulse">Loading history…</div>}
      {!loading && sessions.length === 0 && (
        <p className="text-sm text-[#706E6B]">No import history yet.</p>
      )}
      {sessions.map(s => {
        const ent = ENTITIES.find(e => e.type === s.entity_type);
        return (
          <button
            key={s.id}
            onClick={() => onSelect(s.id)}
            className="w-full flex items-center gap-3 p-3 bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/40 hover:bg-[#EEF4FF]/30 transition-all text-left"
            data-testid={`history-row-${s.id?.slice(0, 8)}`}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: ent?.bgColor || "#F3F3F3" }}>
              {ent ? <ent.icon className="w-4 h-4" style={{ color: ent.color }} /> : <FileSpreadsheet className="w-4 h-4 text-[#706E6B]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-[#181818]">{ent?.label || s.entity_type}</span>
                <StatusBadge status={s.status} />
              </div>
              <div className="text-xs text-[#706E6B] mt-0.5 truncate">
                {s.filename} • {s.committed_rows ?? 0}/{s.total_rows} rows •{" "}
                {new Date(s.uploaded_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ImportManager() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState("new");          // "new" | "history" | "detail"
  const [step, setStep] = useState(0);              // wizard step 0-3
  const [entityType, setEntityType] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const fileInputRef = useRef(null);

  const canAccess = user && hasCapability(user, "admin.users");

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await importApi.listSessions({ limit: 30 });
      setHistory(r.data);
    } catch { } finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadSchema = useCallback(async (type) => {
    try {
      const r = await importApi.getSchema(type);
      setSchema(r.data);
    } catch { }
  }, []);

  useEffect(() => {
    if (entityType) loadSchema(entityType);
  }, [entityType, loadSchema]);

  const handleSelectEntity = (type) => {
    setEntityType(type);
    setStep(1);
    setFile(null);
    setSessionId(null);
    setSessionData(null);
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.match(/\.xlsx?$/i)) {
      toast.error("Please upload an .xlsx or .xls file.");
      return;
    }
    setFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelect(f);
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Please select a file first."); return; }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("entity_type", entityType);
      form.append("file", file);
      if (notes) form.append("notes", notes);
      const r = await importApi.uploadSession(form);
      setSessionId(r.data.session_id);
      toast.success(`${r.data.total_rows} rows parsed from file.`);
      setStep(2);
      // Auto-validate
      await handleValidate(r.data.session_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setLoading(false); }
  };

  const handleValidate = async (sid = sessionId) => {
    if (!sid) return;
    setLoading(true);
    try {
      const r = await importApi.validate(sid);
      setSessionData(r.data);
      toast.success(`Validation complete: ${r.data.valid_rows} valid, ${r.data.error_rows} errors, ${r.data.duplicate_rows} duplicates.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Validation failed.");
    } finally { setLoading(false); }
  };

  const handleCommit = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const r = await importApi.commit(sessionId);
      setSessionData(prev => ({ ...prev, ...r.data }));
      toast.success(`Import committed! ${r.data.committed_rows} records created.`);
      setStep(3);
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Commit failed.");
    } finally { setLoading(false); }
  };

  const handleCancel = async () => {
    if (!sessionId) { resetWizard(); return; }
    try {
      await importApi.cancel(sessionId);
      toast.info("Import session cancelled.");
    } catch { }
    resetWizard();
  };

  const resetWizard = () => {
    setStep(0); setEntityType(""); setFile(null);
    setSessionId(null); setSessionData(null); setNotes("");
  };

  const loadDetail = async (id) => {
    setLoading(true);
    try {
      const r = await importApi.getSession(id);
      setSessionData(r.data);
      setEntityType(r.data.entity_type);
      await loadSchema(r.data.entity_type);
      setView("detail");
    } catch { toast.error("Could not load session."); } finally { setLoading(false); }
  };

  if (!canAccess) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="p-8 bg-[#FEF2F0] border border-[#C23934]/20 rounded-2xl text-center">
          <XCircle className="w-8 h-8 text-[#C23934] mx-auto mb-3" />
          <p className="font-semibold text-[#181818]">Access Denied</p>
          <p className="text-sm text-[#706E6B] mt-1">You need Administration access to use the Import Manager.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="import-manager-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#0176D3]" />
            <h1 className="text-2xl font-bold text-[#181818]">Data Import Manager</h1>
          </div>
          <p className="text-sm text-[#706E6B] mt-1">
            Administration → Data Management → Imports
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setView("new"); resetWizard(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              view === "new" ? "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30" : "border border-[#E5E5E5] text-[#706E6B] hover:border-[#0176D3]/30"
            }`}
            data-testid="import-new-tab">
            <Upload className="w-4 h-4" /> New Import
          </button>
          <button
            onClick={() => { setView("history"); loadHistory(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              view === "history" || view === "detail" ? "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30" : "border border-[#E5E5E5] text-[#706E6B] hover:border-[#0176D3]/30"
            }`}
            data-testid="import-history-tab">
            <History className="w-4 h-4" /> History
          </button>
        </div>
      </div>

      {/* ── VIEW: NEW IMPORT WIZARD ── */}
      {view === "new" && (
        <div className="space-y-6">
          {/* Step progress */}
          <div className="p-4 bg-[#F9F9F9] border border-[#E5E5E5] rounded-2xl">
            <StepIndicator current={step} />
          </div>

          {/* Step 0: Select Entity Type */}
          {step === 0 && (
            <div className="space-y-4" data-testid="import-step-select-type">
              <h2 className="text-lg font-semibold text-[#181818]">What would you like to import?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ENTITIES.map(ent => (
                  <button
                    key={ent.type}
                    onClick={() => handleSelectEntity(ent.type)}
                    className="flex items-start gap-3 p-4 border border-[#E5E5E5] rounded-2xl hover:border-[#0176D3]/50 hover:shadow-sm transition-all text-left group"
                    data-testid={`entity-type-${ent.type}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all"
                         style={{ background: ent.bgColor }}>
                      <ent.icon className="w-5 h-5 transition-all" style={{ color: ent.color }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#181818] group-hover:text-[#0176D3]">{ent.label}</p>
                      <p className="text-xs text-[#706E6B] mt-0.5">{ent.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Upload File */}
          {step === 1 && (
            <div className="space-y-5" data-testid="import-step-upload">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#181818]">
                  Upload {ENTITIES.find(e => e.type === entityType)?.label} File
                </h2>
                <button
                  onClick={() => {
                    const url = `/api/admin/imports/templates/${entityType}`;
                    window.location.href = url;
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl text-sm text-[#0176D3] hover:bg-[#EEF4FF] transition-all font-medium"
                  data-testid="download-template-btn">
                  <Download className="w-4 h-4" /> Download Template
                </button>
              </div>

              {/* Schema hint */}
              {schema && (
                <div className="p-4 bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl">
                  <p className="text-xs font-semibold text-[#706E6B] mb-2">EXPECTED COLUMNS</p>
                  <div className="flex flex-wrap gap-2">
                    {schema.columns.map(c => (
                      <span key={c.col} className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        c.required ? "bg-[#EEF4FF] text-[#0176D3]" : "bg-[#F3F3F3] text-[#706E6B]"
                      }`}>
                        {c.label}{c.required ? " *" : ""}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[#706E6B] mt-2">* Required columns. Data rows start from Row 3 in the template.</p>
                </div>
              )}

              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
                  dragOver ? "border-[#0176D3] bg-[#EEF4FF]" :
                  file ? "border-[#2E844A] bg-[#EEF6EC]" :
                  "border-[#D0D0D0] hover:border-[#0176D3]/50 hover:bg-[#F9F9F9]"
                }`}
                data-testid="file-drop-zone">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                  data-testid="file-input"
                />
                {file ? (
                  <>
                    <CheckCircle2 className="w-10 h-10 text-[#2E844A] mx-auto mb-3" />
                    <p className="font-semibold text-[#181818]">{file.name}</p>
                    <p className="text-sm text-[#706E6B] mt-1">{(file.size / 1024).toFixed(1)} KB — click to change</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-[#D0D0D0] mx-auto mb-3" />
                    <p className="font-semibold text-[#181818]">Drop your Excel file here</p>
                    <p className="text-sm text-[#706E6B] mt-1">or click to browse — .xlsx or .xls, max 10 MB, up to 2,000 rows</p>
                  </>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-semibold text-[#706E6B] block mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Initial customer migration from Excel CRM"
                  className="w-full px-3 py-2 border border-[#E5E5E5] rounded-xl text-sm text-[#181818] focus:outline-none focus:border-[#0176D3] placeholder:text-[#AAAAAA]"
                  data-testid="import-notes-input"
                />
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(0)} className="px-4 py-2 border border-[#E5E5E5] rounded-xl text-sm text-[#706E6B] hover:border-[#0176D3]/30 transition-all">
                  Back
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!file || loading}
                  className="flex items-center gap-2 px-6 py-2 bg-[#0176D3] text-white rounded-xl text-sm font-semibold hover:bg-[#0160B3] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="upload-proceed-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {loading ? "Uploading & Validating…" : "Upload & Preview"}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Preview & Validate */}
          {step === 2 && sessionData && (
            <div className="space-y-5" data-testid="import-step-validate">
              <h2 className="text-lg font-semibold text-[#181818]">Dry-Run Preview</h2>

              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Total Rows",  value: sessionData.total_rows,     color: "#181818" },
                  { label: "Valid",        value: sessionData.valid_rows,      color: "#2E844A" },
                  { label: "Errors",       value: sessionData.error_rows,      color: "#C23934" },
                  { label: "Duplicates",   value: sessionData.duplicate_rows,  color: "#FF8800" },
                ].map(c => (
                  <div key={c.label} className="p-4 bg-[#F9F9F9] border border-[#E5E5E5] rounded-2xl text-center">
                    <p className="text-2xl font-bold" style={{ color: c.color }}>{c.value ?? 0}</p>
                    <p className="text-xs text-[#706E6B] mt-1">{c.label}</p>
                  </div>
                ))}
              </div>

              {sessionData.error_rows > 0 && (
                <div className="flex items-start gap-2 p-3 bg-[#FEF2F0] border border-[#C23934]/20 rounded-xl">
                  <AlertCircle className="w-4 h-4 text-[#C23934] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[#C23934]">
                    {sessionData.error_rows} row{sessionData.error_rows > 1 ? "s" : ""} have errors and will be skipped during commit. Fix them in your file, then re-upload.
                  </p>
                </div>
              )}

              {entityType === "opening_balances" && (
                <div className="flex items-start gap-2 p-3 bg-[#FFF4E0] border border-[#FF8800]/20 rounded-xl">
                  <AlertCircle className="w-4 h-4 text-[#FF8800] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[#FF8800] font-medium">
                    Opening balances are stored as UNPOSTED. Finance authorization is required before any GL posting.
                  </p>
                </div>
              )}

              {/* Row table */}
              {sessionData.rows && sessionData.rows.length > 0 && (
                <RowsTable rows={sessionData.rows} schema={schema} />
              )}

              <div className="flex gap-3 flex-wrap">
                <button onClick={handleCancel} className="px-4 py-2 border border-[#E5E5E5] rounded-xl text-sm text-[#706E6B] hover:border-[#C23934]/30 hover:text-[#C23934] transition-all" data-testid="cancel-import-btn">
                  Cancel
                </button>
                <button
                  onClick={() => handleValidate()}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] rounded-xl text-sm text-[#706E6B] hover:border-[#0176D3]/30 transition-all"
                  data-testid="revalidate-btn">
                  <RefreshCw className="w-3.5 h-3.5" /> Re-Validate
                </button>
                <button
                  onClick={handleCommit}
                  disabled={loading || sessionData.valid_rows === 0}
                  className="flex items-center gap-2 px-6 py-2 bg-[#2E844A] text-white rounded-xl text-sm font-semibold hover:bg-[#256B3A] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="commit-import-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {loading ? "Committing…" : `Commit ${sessionData.valid_rows} Record${sessionData.valid_rows !== 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && sessionData && (
            <div className="text-center py-12 space-y-5" data-testid="import-step-complete">
              <div className="w-16 h-16 rounded-full bg-[#EEF6EC] flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-[#2E844A]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#181818]">Import Complete</h2>
                <p className="text-sm text-[#706E6B] mt-2">
                  <span className="font-semibold text-[#2E844A]">{sessionData.committed_rows}</span> records created.
                  {sessionData.skipped_rows > 0 && ` ${sessionData.skipped_rows} rows skipped.`}
                </p>
                {entityType === "opening_balances" && (
                  <p className="text-xs text-[#FF8800] mt-2 font-medium">
                    Opening balances are UNPOSTED. Notify Finance to authorize posting.
                  </p>
                )}
              </div>
              <div className="flex gap-3 justify-center">
                <button onClick={resetWizard} className="flex items-center gap-2 px-6 py-2 bg-[#0176D3] text-white rounded-xl text-sm font-semibold hover:bg-[#0160B3] transition-all" data-testid="import-again-btn">
                  <Upload className="w-4 h-4" /> Import More
                </button>
                <button onClick={() => { setView("history"); loadHistory(); }} className="flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] rounded-xl text-sm text-[#706E6B] hover:border-[#0176D3]/30 transition-all" data-testid="view-history-btn">
                  <History className="w-4 h-4" /> View History
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── VIEW: HISTORY ── */}
      {view === "history" && (
        <div className="space-y-4" data-testid="import-history-view">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#181818]">Import History</h2>
            <button onClick={loadHistory} className="p-2 rounded-lg border border-[#E5E5E5] text-[#706E6B] hover:text-[#0176D3] transition-all" data-testid="refresh-history-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <HistoryPanel sessions={history} onSelect={loadDetail} loading={historyLoading} />
        </div>
      )}

      {/* ── VIEW: SESSION DETAIL ── */}
      {view === "detail" && sessionData && (
        <div className="space-y-5" data-testid="import-detail-view">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView("history"); loadHistory(); }} className="text-sm text-[#0176D3] hover:underline flex items-center gap-1">
              ← History
            </button>
            <span className="text-[#D0D0D0]">/</span>
            <span className="text-sm text-[#181818] font-medium">
              {ENTITIES.find(e => e.type === sessionData.entity_type)?.label}
            </span>
            <StatusBadge status={sessionData.status} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "Total",      value: sessionData.total_rows      },
              { label: "Valid",      value: sessionData.valid_rows,   color: "#2E844A" },
              { label: "Errors",     value: sessionData.error_rows,   color: "#C23934" },
              { label: "Duplicates", value: sessionData.duplicate_rows, color: "#FF8800" },
              { label: "Committed",  value: sessionData.committed_rows, color: "#2E844A" },
              { label: "File",       value: sessionData.filename, small: true },
            ].map(c => (
              <div key={c.label} className="p-3 bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl text-center">
                <p className={`font-bold ${c.small ? "text-xs" : "text-xl"} truncate`} style={{ color: c.color || "#181818" }}>{c.value ?? 0}</p>
                <p className="text-xs text-[#706E6B] mt-0.5">{c.label}</p>
              </div>
            ))}
          </div>

          {sessionData.rows && sessionData.rows.length > 0 && (
            <RowsTable rows={sessionData.rows} schema={schema} />
          )}
        </div>
      )}
    </div>
  );
}
